import { ldb } from '@codesuma/baseline'
import db from './db'
import utils from '../utils'
import listsService, { UserList } from './lists'
import itemOrder from './item-order'
import helpers from '../helpers'

// Curated public lists ("Discover") and the Save-a-copy flow. Saving creates a
// private, independent copy the same way the starter seed does: a new local
// list (real UUID) + temp_ items, all synced:false, pushed to the server lazily
// on the user's first edit (list.ts flush()). No account or session is needed
// to browse or save.

export interface PublicListEntry {
    id: string
    slug: string
    title: string
    description: string
    category: string
    saves: number
    item_count: number
}

export interface PublicListItem {
    name: string
    kind?: 'item' | 'heading'
}

export interface PublicList extends Omit<PublicListEntry, 'item_count'> {
    items: PublicListItem[]
}

// Last fetched catalog, kept in localStorage so the home strip paints instantly
// on the next launch (and still shows something offline).
const CATALOG_KEY = 'papa-public-catalog'
const getCachedCatalog = (): PublicListEntry[] => ldb.get(CATALOG_KEY) || []

// Memoized for the session — the home page rebuilds its strip on every render
// (baseline empty() is permanent), and curated content barely changes. A failed
// fetch clears the memo so a retry (or the next render) tries again.
let catalogPromise: Promise<PublicListEntry[]> | null = null

const getCatalog = (): Promise<PublicListEntry[]> => {
    if (!catalogPromise) {
        catalogPromise = utils.http.get<{ lists: PublicListEntry[] }>('/api/v1/public-lists')
            .then(res => {
                const lists = res.lists || []
                ldb.set(CATALOG_KEY, lists)
                return lists
            })
            .catch(err => { catalogPromise = null; throw err })
    }
    return catalogPromise
}

const getBySlug = async (slug: string): Promise<PublicList | null> => {
    const res = await utils.http.get<{ list: PublicList }>(`/api/v1/public-lists/${slug}`)
    return res.list || null
}

// Save a private copy of a public list. Returns the new list's id.
// created_at stamps are staggered downward (like seed.ts) so the listed order
// survives the newest-first sort on any device once synced — sections depend on
// order, since an item belongs to the heading above it.
const saveCopy = async (pub: PublicList): Promise<string> => {
    await db.init()
    const listId = crypto.randomUUID()
    const now = Date.now()
    const tasks = pub.items.filter(i => i.kind !== 'heading')

    // The copy keeps the color it wore on Discover (slug-hashed palette slot) —
    // continuity from the card to "my list". Carried to the server on first sync.
    const color = helpers.paletteIndexForId(pub.slug)
    helpers.setListColor(listId, color)

    const list: UserList = {
        id: listId,
        name: pub.title,
        created_by: '',
        created_at: new Date(now).toISOString(),
        role: 'owner',
        item_count: tasks.length,
        done_count: 0,
        member_count: 1,
        synced: false,
        color,
    }
    await db.save(db.LIST_STORE, list)

    const ids: string[] = []
    for (let i = 0; i < pub.items.length; i++) {
        const it = pub.items[i]
        const id = 'temp_' + now + '_' + i + '_' + Math.random().toString(36).substring(2, 7)
        const ts = new Date(now - i * 1000).toISOString()
        ids.push(id)
        await db.save(db.STORE, {
            id,
            list_id: listId,
            name: it.name,
            checked: false,
            category: 'General',
            kind: it.kind === 'heading' ? 'heading' : 'item',
            user_id: '',
            synced: false,
            created_at: ts,
            updated_at: ts,
        })
    }

    // Pin the section layout from the first open. Local only — the copy has no
    // server row yet, and item-order skips the push for exactly that reason.
    itemOrder.setLocal(listId, ids)

    // Tell the app the list exists — the home grid updates even if it was
    // already rendered, and its cache no longer lags behind IndexedDB.
    listsService.registerLocal(list)

    // Count the save (fire-and-forget — the copy exists either way).
    utils.http.post(`/api/v1/public-lists/${pub.slug}/save`, {}).catch(() => { /* offline — fine */ })

    return listId
}

export default { getCatalog, getCachedCatalog, getBySlug, saveCopy }
